import { useEffect, useRef, useState } from 'react';
import { useContactContext } from "../context/ContactContext";

const Contact = () => {
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [message, setMessage] = useState("");
    const [isSending, setIsSending] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalType, setModalType] = useState("");
    const contactContext = useContactContext();
    const dialogRef = useRef(null);
    const closeButtonRef = useRef(null);

    useEffect(() => {
        if (isModalOpen && !dialogRef.current?.open) {
            dialogRef.current?.showModal();
            closeButtonRef.current?.focus();
        }
    }, [isModalOpen]);

    const closeModal = () => {
        if (dialogRef.current?.open) {
            dialogRef.current.close();
        } else {
            setIsModalOpen(false);
        }
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (isSending) return;

        setIsSending(true);
        try {
            const isSent = await contactContext.sendMessage(email.trim(), name.trim(), message.trim());
            setModalType(isSent ? "success" : "error");
            setIsModalOpen(true);

            if (isSent) {
                setMessage("");
                setName("");
                setEmail("");
            }
        } catch (_) {
            setModalType("error");
            setIsModalOpen(true);
        } finally {
            setIsSending(false);
        }
    };

    return (
        <section id="contact" className="px-4 py-16 transition-colors duration-300 sm:px-6 sm:py-20 lg:px-8 lg:py-28 bg-gradient-to-b from-white to-slate-50 dark:from-slate-900 dark:to-slate-950">
            <div className="mx-auto max-w-6xl">
                <header className="mb-10 max-w-3xl sm:mb-14">
                    <p className="mb-3 text-sm font-bold uppercase tracking-wide text-blue-600 dark:text-blue-400">
                        Get in touch
                    </p>
                    <h1 className="mb-5 text-4xl font-bold text-slate-900 dark:text-white sm:text-5xl md:text-6xl">
                        Let's connect
                    </h1>
                    <p className="text-lg leading-relaxed text-slate-600 dark:text-slate-300 sm:text-xl">
                        Have a project, opportunity, or idea to discuss? Send me a note.
                    </p>
                </header>

                <form onSubmit={handleSubmit} aria-busy={isSending} className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-700 dark:bg-slate-800 sm:p-8">
                    <div className="mb-6">
                        <label htmlFor="contact-name" className="mb-2 block text-sm font-semibold text-slate-900 dark:text-white">
                            Name
                        </label>
                        <input
                            type="text"
                            id="contact-name"
                            placeholder="Your name"
                            autoComplete="name"
                            required
                            onChange={(event) => setName(event.target.value)}
                            value={name}
                            className="w-full rounded-md border border-slate-300 bg-white px-4 py-3 text-base text-slate-900 placeholder-slate-400 transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 dark:border-slate-600 dark:bg-slate-700 dark:text-white dark:placeholder-slate-400 dark:focus-visible:ring-blue-400"
                        />
                    </div>

                    <div className="mb-6">
                        <label htmlFor="contact-email" className="mb-2 block text-sm font-semibold text-slate-900 dark:text-white">
                            Email
                        </label>
                        <input
                            type="email"
                            id="contact-email"
                            placeholder="youremail@email.com"
                            autoComplete="email"
                            required
                            onChange={(event) => setEmail(event.target.value)}
                            value={email}
                            className="w-full rounded-md border border-slate-300 bg-white px-4 py-3 text-base text-slate-900 placeholder-slate-400 transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 dark:border-slate-600 dark:bg-slate-700 dark:text-white dark:placeholder-slate-400 dark:focus-visible:ring-blue-400"
                        />
                    </div>

                    <div className="mb-6">
                        <label htmlFor="contact-message" className="mb-2 block text-sm font-semibold text-slate-900 dark:text-white">
                            Message
                        </label>
                        <textarea
                            id="contact-message"
                            placeholder="Let's work on something cool!"
                            required
                            onChange={(event) => setMessage(event.target.value)}
                            value={message}
                            rows={5}
                            className="w-full resize-y rounded-md border border-slate-300 bg-white px-4 py-3 text-base leading-relaxed text-slate-900 placeholder-slate-400 transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 dark:border-slate-600 dark:bg-slate-700 dark:text-white dark:placeholder-slate-400 dark:focus-visible:ring-blue-400"
                        />
                    </div>

                    <button
                        type="submit"
                        disabled={isSending}
                        className="inline-flex min-h-12 w-full items-center justify-center rounded-md bg-blue-700 px-6 py-3 text-base font-semibold text-white transition-colors hover:bg-blue-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2 disabled:cursor-wait disabled:opacity-70 dark:bg-blue-600 dark:hover:bg-blue-500 dark:focus-visible:ring-blue-400 dark:focus-visible:ring-offset-slate-800 sm:w-auto"
                    >
                        {isSending ? "Sending…" : "Send message"}
                    </button>
                </form>
            </div>

            {isModalOpen && (
                <dialog
                    ref={dialogRef}
                    onClose={() => setIsModalOpen(false)}
                    role={modalType === "error" ? "alertdialog" : "dialog"}
                    aria-labelledby="contact-feedback-title"
                    aria-describedby="contact-feedback-message"
                    className="m-auto w-[calc(100%-2rem)] max-w-md overflow-hidden rounded-lg border border-slate-200 bg-white p-0 shadow-2xl backdrop:bg-slate-950/60 dark:border-slate-700 dark:bg-slate-800"
                >
                    <div className={`border-b px-6 py-4 ${modalType === "success" ? "border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-950" : "border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-950"}`}>
                        <h2 id="contact-feedback-title" className={`text-xl font-bold sm:text-2xl ${modalType === "success" ? "text-green-900 dark:text-green-100" : "text-red-900 dark:text-red-100"}`}>
                            {modalType === "success" ? "Message sent" : "Message not sent"}
                        </h2>
                    </div>
                    <div className="space-y-3 px-6 py-6">
                        <p id="contact-feedback-message" className="text-base leading-relaxed text-slate-700 dark:text-slate-300">
                            {modalType === "success"
                                ? "Thanks for reaching out. I'll get back to you soon."
                                : "Your message couldn't be sent. Please try again later or contact me directly."}
                        </p>
                        {modalType === "error" && (
                            <a
                                href="https://www.linkedin.com/in/ayushkaushik"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex min-h-11 items-center font-semibold text-blue-700 underline underline-offset-4 hover:text-blue-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 dark:text-blue-300 dark:hover:text-blue-200 dark:focus-visible:ring-blue-400"
                            >
                                Contact me on LinkedIn
                            </a>
                        )}
                    </div>
                    <div className="flex justify-end border-t border-slate-200 px-6 py-4 dark:border-slate-700">
                        <button
                            ref={closeButtonRef}
                            type="button"
                            onClick={closeModal}
                            className="inline-flex min-h-11 items-center rounded-md bg-slate-100 px-5 font-semibold text-slate-800 transition-colors hover:bg-slate-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 dark:bg-slate-700 dark:text-white dark:hover:bg-slate-600 dark:focus-visible:ring-blue-400"
                        >
                            Close
                        </button>
                    </div>
                </dialog>
            )}
        </section>
    );
};

export default Contact;